import utils from "../utils";

/**
 * Parse CSV file content into login details
 * Supports common formats from LastPass, 1Password, Chrome, Firefox
 */
export function parseCSV(csvContent, importName) {
  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file is empty or invalid');
  }

  const headers = parseCSVLine(lines[0]);
  const records = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length === 0) continue;

    const record = {};
    headers.forEach((header, index) => {
      record[header.toLowerCase().trim()] = values[index] || '';
    });

    // Map common field names to our format
    const loginDetail = {
      websiteName: record.name || record.title || record.website || record.url || 'Imported Item',
      loginId: record.username || record.login || record.email || '',
      password: record.password || '',
      websiteUrl: record.url || record.website || record.login_uri || '',
      importSource: importName, // Track where this came from
      importDate: new Date().toISOString()
    };

    // Only add if we have at least a website name
    if (loginDetail.websiteName) {
      records.push(loginDetail);
    }
  }

  return records;
}

/**
 * Parse a single CSV line, handling quoted values
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current);
  return result;
}

/**
 * Parse JSON file content into login details
 */
export function parseJSON(jsonContent, importName) {
  try {
    const data = JSON.parse(jsonContent);
    const records = Array.isArray(data) ? data : [data];

    return records.map(record => ({
      websiteName: record.websiteName || record.name || record.title || 'Imported Item',
      loginId: record.loginId || record.username || record.email || '',
      password: record.password || '',
      websiteUrl: record.websiteUrl || record.url || '',
      importSource: importName,
      importDate: new Date().toISOString()
    }));
  } catch (error) {
    throw new Error('Invalid JSON format: ' + error.message);
  }
}

/**
 * Export login details to CSV format
 */
export function exportToCSV(loginDetails) {
  const headers = ['Website Name', 'Username', 'Password', 'URL', 'Import Source', 'Import Date'];
  const rows = [headers.join(',')];

  loginDetails.forEach(detail => {
    const dateValue = detail.importDate || detail.createdAt;
    const readableDate = dateValue ? new Date(dateValue).toISOString() : '';
    
    const row = [
      escapeCSV(utils.decrypt(detail.websiteName)),
      escapeCSV(utils.decrypt(detail.loginId)),
      escapeCSV(utils.decrypt(detail.password)),
      escapeCSV(utils.decrypt(detail.websiteUrl)),
      escapeCSV(detail.importSource || 'Manual'),
      escapeCSV(readableDate)
    ];
    rows.push(row.join(','));
  });

  return rows.join('\n');
}

/**
 * Export login details to JSON format
 */
export function exportToJSON(loginDetails) {
  const data = loginDetails.map(detail => {
    const dateValue = detail.importDate || detail.createdAt;
    const readableDate = dateValue ? new Date(dateValue).toISOString() : '';
    
    return {
      websiteName: utils.decrypt(detail.websiteName),
      loginId: utils.decrypt(detail.loginId),
      password: utils.decrypt(detail.password),
      websiteUrl: utils.decrypt(detail.websiteUrl),
      importSource: detail.importSource || 'Manual',
      importDate: readableDate
    };
  });

  return JSON.stringify(data, null, 2);
}

/**
 * Escape CSV value
 */
function escapeCSV(value) {
  if (!value) return '';
  const stringValue = String(value);
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

/**
 * Download file to user's computer
 */
export function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
